import React, { useState, useEffect } from "react";
import {
  Container, Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, FormControl, InputLabel, MenuItem, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Alert, Tabs, Tab, Grid, Box, Card, CardContent,
  Radio, RadioGroup, FormControlLabel, IconButton, Tooltip, Checkbox, CircularProgress,
  Divider, Badge, Stack, Avatar, InputAdornment, Snackbar
} from "@mui/material";
import {
  AutoAwesome as SparklesIcon,
  AutoAwesomeMotion as WandIcon,
  Add as PlusIcon,
  Delete as TrashIcon,
  ExpandMore as ChevronDownIcon,
  MenuBook as BookOpenIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  School as SchoolIcon,
  Psychology as PsychologyIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function TestQuestionManagement() {
  const [questions, setQuestions] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [openAIDialog, setOpenAIDialog] = useState(false);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [bulkQuestions, setBulkQuestions] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
  
  const [filterSkillCategory, setFilterSkillCategory] = useState("");
  const [filterSkillContent, setFilterSkillContent] = useState("");
  
  const [bulkSkillCategory, setBulkSkillCategory] = useState("");
  const [bulkSkillContent, setBulkSkillContent] = useState("");
  const [showManualBulkCategory, setShowManualBulkCategory] = useState(false);
  const [showManualBulkContent, setShowManualBulkContent] = useState(false);
  const [manualBulkCategory, setManualBulkCategory] = useState("");
  const [manualBulkContent, setManualBulkContent] = useState("");

  const [aiConfig, setAiConfig] = useState({
    skillCategory: "",
    skillContent: "",
    numberOfQuestions: 5,
    difficulty: "Medium",
    videoId: "",
    useVideoTranscript: false
  });

  const [showManualAICategory, setShowManualAICategory] = useState(false);
  const [showManualAIContent, setShowManualAIContent] = useState(false);
  const [manualAICategory, setManualAICategory] = useState("");
  const [manualAIContent, setManualAIContent] = useState("");

  const skillOptions = [
    "Process Management", "People Management", "Interpersonal Skills",
    "Communication Skills", "Managing Skills", "Technical Skills",
    "Cultural Skills", "Business Skills", "Accounting Skills", "Industrial Certificates",
  ];

  const skillContents = {
    "Process Management": ["Workflow Optimization", "Lean Six Sigma", "Agile Methodology"],
    "People Management": ["Team Leadership", "Conflict Resolution", "Performance Evaluation"],
    "Interpersonal Skills": ["Empathy Building", "Active Listening", "Networking Strategies"],
    "Communication Skills": ["Business Writing", "Presentation Skills", "Negotiation Techniques"],
    "Managing Skills": ["Time Management", "Risk Management", "Decision Making"],
    "Technical Skills": ["Python Programming", "Cloud Computing", "Data Analysis", "Machine Learning"],
    "Cultural Skills": ["Diversity Training", "Cross-Cultural Communication", "Inclusive Leadership"],
    "Business Skills": ["Market Analysis", "Strategic Planning", "Entrepreneurship"],
    "Accounting Skills": ["Financial Reporting", "Tax Planning", "Budgeting"],
    "Industrial Certificates": ["AWS Certified", "PMP Certification", "ISO Compliance"]
  };

  const difficultyLevels = ["Easy", "Medium", "Hard"];

  useEffect(() => {
    fetchQuestions();
    fetchVideos();
  }, []);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess("");
        setError("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/test-questions`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // Ensure correctAnswer is a number
      const processedQuestions = response.data.map(q => ({
        ...q,
        correctAnswer: Number(q.correctAnswer) || 0
      }));
      setQuestions(processedQuestions);
    } catch (error) {
      setError("Failed to fetch test questions");
    } finally {
      setLoading(false);
    }
  };

  const fetchVideos = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/training-videos`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setVideos(response.data);
    } catch (error) {
    }
  };

  const handleAICategoryChange = (e) => {
    const value = e.target.value;
    
    if (value === 'manual') {
      setShowManualAICategory(true);
      setShowManualAIContent(false);
      setAiConfig({
        ...aiConfig,
        skillCategory: '',
        skillContent: ''
      });
    } else {
      setShowManualAICategory(false);
      setAiConfig({
        ...aiConfig,
        skillCategory: value,
        skillContent: ''
      });
    }
  };

  const handleAIContentChange = (e) => {
    const value = e.target.value;
    
    if (value === 'manual') {
      setShowManualAIContent(true);
      setAiConfig({
        ...aiConfig,
        skillContent: ''
      });
    } else {
      setShowManualAIContent(false);
      setAiConfig({
        ...aiConfig,
        skillContent: value
      });
    }
  };

  const handleManualAICategoryChange = (e) => {
    const value = e.target.value;
    setManualAICategory(value);
    setAiConfig({
      ...aiConfig,
      skillCategory: value
    });
  };

  const handleManualAIContentChange = (e) => {
    const value = e.target.value;
    setManualAIContent(value);
    setAiConfig({
      ...aiConfig,
      skillContent: value
    });
  };

  const handleBulkCategoryChange = (e) => {
    const value = e.target.value;
    
    if (value === 'manual') {
      setShowManualBulkCategory(true);
      setShowManualBulkContent(false);
      setBulkSkillCategory('');
      setBulkSkillContent('');
    } else {
      setShowManualBulkCategory(false);
      setBulkSkillCategory(value);
      setBulkSkillContent('');
    }
  };

  const handleBulkContentChange = (e) => {
    const value = e.target.value;
    
    if (value === 'manual') {
      setShowManualBulkContent(true);
      setBulkSkillContent('');
    } else {
      setShowManualBulkContent(false);
      setBulkSkillContent(value);
    }
  };
// Add this helper function in your frontend
const getErrorMessage = (error) => {
  if (error.response?.data?.message) {
    const msg = error.response.data.message;
    // Check for specific known errors
    if (msg.includes("busy")) {
      return "The AI service is currently busy. Please try again in a few moments.";
    }
    if (msg.includes("quota")) {
      return "AI service quota exceeded. Please try again later.";
    }
    return msg;
  }
  return "Failed to generate questions. Please try again.";
};


const generateQuestionsWithAI = async () => {
    const finalSkillCategory = showManualAICategory ? manualAICategory : aiConfig.skillCategory;
    const finalSkillContent = showManualAIContent ? manualAIContent : aiConfig.skillContent;
    
    if (!finalSkillCategory || !finalSkillContent) {
      setAlert({
        open: true,
        message: "Please select skill category and content",
        severity: "warning"
      });
      return;
    }

    setAiLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setAlert({
          open: true,
          message: "No authentication token found. Please log in.",
          severity: "error"
        });
        return;
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

  

      const response = await axios.post(
        `${API_URL}/api/test-questions/generate-ai`,
        {
          ...aiConfig,
          skillCategory: finalSkillCategory,
          skillContent: finalSkillContent,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      
      if (response.status === 200) {
        // Ensure correctAnswer is number
        const processedQuestions = (response.data.questions || []).map(q => ({
          ...q,
          correctAnswer: Number(q.correctAnswer) || 0
        }));
      
        setBulkQuestions(processedQuestions);
        setBulkSkillCategory(finalSkillCategory);
        setBulkSkillContent(finalSkillContent);
        setAlert({
          open: true,
          message: `✨ AI generated ${processedQuestions.length} questions successfully!`,
          severity: "success"
        });
        setOpenAIDialog(false);
        setOpenBulkDialog(true);
        
        // Reset AI form
        setAiConfig({
          skillCategory: "",
          skillContent: "",
          numberOfQuestions: 5,
          difficulty: "Medium",
          videoId: "",
          useVideoTranscript: false
        });
        setManualAICategory("");
        setManualAIContent("");
        setShowManualAICategory(false);
        setShowManualAIContent(false);
      } else {
        setAlert({
          open: true,
          message: response.data.message || "Failed to generate questions",
          severity: "error"
        });
      }
    } catch (error) {
      const errorData = error.response?.data;

      // 1. IF PLAN DOES NOT INCLUDE AI (e.g., Basic Plan with 0 tokens)
      if (errorData?.code === "AI_PLAN_UPGRADE_REQUIRED") {
        setAlert({
          open: true,
          message: errorData.message || "AI features are not included in your current plan. Please upgrade to unlock AI.",
          severity: "warning"
        });
      } 
      // 2. IF TOKENS ARE EXHAUSTED
      else if (errorData?.code === "AI_TOKENS_EXHAUSTED") {
        setAlert({
          open: true,
          message: `⚠️ ${errorData.message || "You have used all your AI tokens for this billing cycle."}`,
          severity: "warning"
        });
      } 
      else {
        let errorMessage = "Failed to generate questions with AI. Please try again.";
        if (errorData?.message) {
          errorMessage = errorData.message;
        }
        setAlert({
          open: true,
          message: errorMessage,
          severity: "error"
        });
      }
    } finally {
      setAiLoading(false);
    }
  };

  const addBulkQuestions = async () => {
    const finalSkillCategory = showManualBulkCategory ? manualBulkCategory : bulkSkillCategory;
    const finalSkillContent = showManualBulkContent ? manualBulkContent : bulkSkillContent;
    
    if (!finalSkillCategory || !finalSkillContent || bulkQuestions.length === 0) {
      setError("Please fill all required fields and add at least one question");
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }

    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;

    try {
      // Ensure correctAnswer is number
      const questionsToAdd = bulkQuestions.map(q => ({
        ...q,
        skillCategory: finalSkillCategory,
        skillContent: finalSkillContent,
        correctAnswer: Number(q.correctAnswer) || 0
      }));

      const response = await axios.post(
        `${API_URL}/api/test-questions/bulk`,

        { questions: questionsToAdd },{
        
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status >= 200 && response.status < 300) {
        setSuccess(`Successfully added ${bulkQuestions.length} questions`);
        setOpenBulkDialog(false);
        resetBulkForm();
        fetchQuestions();
      } else {
        setError(response.data.message || "Failed to add bulk questions");
      }
    } catch (error) {
      setError(error.response?.data?.message || "Failed to add bulk questions");
    }
  };

  const resetBulkForm = () => {
    setBulkSkillCategory("");
    setBulkSkillContent("");
    setBulkQuestions([]);
    setManualBulkCategory("");
    setManualBulkContent("");
    setShowManualBulkCategory(false);
    setShowManualBulkContent(false);
  };

  const deleteQuestion = async (questionId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

      await axios.delete(`${API_URL}/api/test-questions/${questionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccess("Test question deleted successfully!");
      fetchQuestions();
    } catch (error) {
      setError("Failed to delete test question");
    }
  };

  const addEmptyQuestion = () => {
    setBulkQuestions([...bulkQuestions, {
      question: "",
      options: ["", ""],
      correctAnswer: 0,
      explanation: "",
      difficulty: "Medium",
      videoId: ""
    }]);
  };

  const handleBulkQuestionChange = (index, field, value) => {
    const updatedQuestions = [...bulkQuestions];
    if (field === 'correctAnswer') {
      updatedQuestions[index] = { ...updatedQuestions[index], [field]: Number(value) || 0 };
    } else {
      updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
    }
    setBulkQuestions(updatedQuestions);
  };

  const handleBulkOptionChange = (questionIndex, optionIndex, value) => {
    const updatedQuestions = [...bulkQuestions];
    const options = [...updatedQuestions[questionIndex].options];
    options[optionIndex] = value;
    updatedQuestions[questionIndex] = { ...updatedQuestions[questionIndex], options };
    setBulkQuestions(updatedQuestions);
  };

  const addBulkOption = (questionIndex) => {
    if (bulkQuestions[questionIndex].options.length < 6) {
      const updatedQuestions = [...bulkQuestions];
      updatedQuestions[questionIndex] = {
        ...updatedQuestions[questionIndex],
        options: [...updatedQuestions[questionIndex].options, ""]
      };
      setBulkQuestions(updatedQuestions);
    } else {
      setError("Maximum 6 options allowed");
    }
  };

  const removeBulkOption = (questionIndex, optionIndex) => {
    if (bulkQuestions[questionIndex].options.length > 2) {
      const updatedQuestions = [...bulkQuestions];
      const newOptions = updatedQuestions[questionIndex].options.filter((_, i) => i !== optionIndex);
      
      let newCorrectAnswer = Number(updatedQuestions[questionIndex].correctAnswer);
      if (optionIndex === newCorrectAnswer) {
        newCorrectAnswer = 0;
      } else if (optionIndex < newCorrectAnswer) {
        newCorrectAnswer -= 1;
      }
      
      updatedQuestions[questionIndex] = {
        ...updatedQuestions[questionIndex],
        options: newOptions,
        correctAnswer: newCorrectAnswer
      };
      
      setBulkQuestions(updatedQuestions);
    } else {
      setError("Minimum 2 options required");
    }
  };

  const removeBulkQuestion = (index) => {
    const updatedQuestions = bulkQuestions.filter((_, i) => i !== index);
    setBulkQuestions(updatedQuestions);
  };

  const filteredQuestions = questions.filter(q => {
    return (!filterSkillCategory || q.skillCategory === filterSkillCategory) &&
           (!filterSkillContent || q.skillContent === filterSkillContent);
  });

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setFilterSkillCategory(newValue === 0 ? "" : skillOptions[newValue - 1]);
    setFilterSkillContent("");
  };

  const getDifficultyColor = (difficulty) => {
    switch(difficulty) {
      case 'Easy': return { 
        backgroundColor: '#dcfce7', 
        color: '#166534',
        borderColor: '#86efac'
      };
      case 'Medium': return { 
        backgroundColor: '#dbeafe', 
        color: '#1e40af',
        borderColor: '#93c5fd'
      };
      case 'Hard': return { 
        backgroundColor: '#fee2e2', 
        color: '#991b1b',
        borderColor: '#fca5a5'
      };
      default: return { 
        backgroundColor: '#f3f4f6', 
        color: '#374151',
        borderColor: '#d1d5db'
      };
    }
  };

  // Debug function to check question data
  const debugQuestion = (question) => {
 
  };

  return (
    <Box sx={{ minHeight: '100vh', py: 3 }}>
      <Container maxWidth="xl">
        {/* Alerts */}
        {success && (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              '& .MuiAlert-icon': { fontSize: 28 }
            }}
          >
            {success}
          </Alert>
        )}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              '& .MuiAlert-icon': { fontSize: 28 }
            }}
          >
            {error}
          </Alert>
        )}

        {/* Main Card */}
        <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
          {/* Header */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            p: 4,
            color: 'white'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
                  Add Test Questions
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Create, manage and organize assessment questions for your training programs
                </Typography>
              </Box>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<SparklesIcon />}
                  onClick={() => setOpenAIDialog(true)}
                  sx={{
                    background: 'linear-gradient(45deg, #f093fb 0%, #f5576c 100%)',
                    color: 'white',
                    boxShadow: '0 8px 24px rgba(245, 87, 108, 0.3)',
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    '&:hover': { 
                      background: 'linear-gradient(45deg, #e082ea 0%, #e4465b 100%)',
                      boxShadow: '0 12px 32px rgba(245, 87, 108, 0.4)',
                      transform: 'translateY(-2px)',
                      transition: 'all 0.3s ease'
                    }
                  }}
                >
                  AI Generate
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlusIcon />}
                  onClick={() => setOpenBulkDialog(true)}
                  sx={{
                    bgcolor: 'white',
                    color: '#667eea',
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    '&:hover': { 
                      bgcolor: '#f8f9fa',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                      transition: 'all 0.3s ease'
                    }
                  }}
                >
                  Add Questions
                </Button>
              </Stack>
            </Box>
          </Box>

          <Box sx={{ p: 4 }}>
            {/* Tabs */}
            <Paper elevation={0} sx={{ mb: 4, borderRadius: 2, bgcolor: '#f8f9fa' }}>
              <Tabs 
                value={currentTab} 
                onChange={handleTabChange} 
                variant="scrollable" 
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    minHeight: 56,
                    px: 3
                  },
                  '& .Mui-selected': {
                    color: '#667eea'
                  },
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                  }
                }}
              >
                <Tab label={`All Questions (${questions.length})`} />
                {skillOptions.map((skill, index) => {
                  const count = questions.filter(q => q.skillCategory === skill).length;
                  return (
                    <Tab 
                      key={index} 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {skill}
                          <Chip 
                            label={count} 
                            size="small" 
                            sx={{ 
                              height: 20, 
                              fontSize: '0.75rem',
                              bgcolor: currentTab === index + 1 ? '#667eea' : '#e0e0e0',
                              color: currentTab === index + 1 ? 'white' : 'inherit'
                            }} 
                          />
                        </Box>
                      }
                    />
                  );
                })}
              </Tabs>
            </Paper>

            {/* Content Filter Chips */}
            {currentTab > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                  Filter by Content:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2 }}>
                  <Chip
                    label="All Content"
                    onClick={() => setFilterSkillContent("")}
                    sx={{
                      bgcolor: !filterSkillContent ? '#667eea' : 'white',
                      color: !filterSkillContent ? 'white' : '#667eea',
                      border: '2px solid',
                      borderColor: !filterSkillContent ? '#667eea' : '#e0e0e0',
                      fontWeight: 600,
                      px: 1,
                      '&:hover': {
                        bgcolor: !filterSkillContent ? '#5568d3' : '#f5f5f5',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  />
                  {skillContents[skillOptions[currentTab - 1]]?.map((content, idx) => (
                    <Chip
                      key={idx}
                      label={content}
                      onClick={() => setFilterSkillContent(content)}
                      sx={{
                        bgcolor: filterSkillContent === content ? '#667eea' : 'white',
                        color: filterSkillContent === content ? 'white' : '#667eea',
                        border: '2px solid',
                        borderColor: filterSkillContent === content ? '#667eea' : '#e0e0e0',
                        fontWeight: 600,
                        px: 1,
                        '&:hover': {
                          bgcolor: filterSkillContent === content ? '#5568d3' : '#f5f5f5',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Questions List */}
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                py: 12 
              }}>
                <CircularProgress size={60} thickness={4} sx={{ color: '#667eea', mb: 3 }} />
                <Typography variant="h6" color="text.secondary">
                  Loading questions...
                </Typography>
              </Box>
            ) : filteredQuestions.length === 0 ? (
              <Box sx={{ 
                py: 12, 
                textAlign: 'center',
                border: '2px dashed #e0e0e0',
                borderRadius: 3,
                bgcolor: '#fafafa'
              }}>
                <SchoolIcon sx={{ fontSize: 80, color: '#d0d0d0', mb: 2 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  No Questions Found
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {currentTab === 0 
                    ? "Get started by adding questions or using AI to generate them"
                    : "No questions found with the selected filters"}
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#374151' }}>
                        Question
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#374151' }}>
                        Category
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#374151' }}>
                        Difficulty
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#374151' }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredQuestions.map((question, index) => {
                      const correctIndex = Number(question.correctAnswer) || 0;
                      return (
                        <React.Fragment key={question.id}>
                          <TableRow 
                            hover 
                            sx={{ 
                              '&:hover': { bgcolor: '#f8f9fa' },
                              borderBottom: expandedQuestion === question.id ? 'none' : '1px solid #e0e0e0'
                            }}
                          >
                            <TableCell sx={{ py: 2.5 }}>
                              <Box 
                                sx={{ cursor: 'pointer' }} 
                                onClick={() => {
                                  debugQuestion(question); // Debug log
                                  setExpandedQuestion(expandedQuestion === question.id ? null : question.id);
                                }}
                              >
                                <Typography variant="body1" fontWeight={500} sx={{ mb: 0.5 }}>
                                  {index + 1}. {question.question.length > 120 
                                    ? question.question.substring(0, 120) + "..." 
                                    : question.question}
                                </Typography>
                                {question.skillContent && (
                                  <Typography variant="caption" color="text.secondary">
                                    {question.skillContent}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Chip 
                                label={question.skillCategory} 
                                size="small" 
                                sx={{
                                  bgcolor: '#e8eaf6',
                                  color: '#3f51b5',
                                  fontWeight: 600,
                                  border: '1px solid #c5cae9'
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={question.difficulty}
                                size="small"
                                sx={{
                                  ...getDifficultyColor(question.difficulty),
                                  fontWeight: 600,
                                  border: `1px solid ${getDifficultyColor(question.difficulty).borderColor}`,
                                  px: 1.5
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Stack direction="row" spacing={1} justifyContent="center">
                                <Tooltip title="Delete Question" arrow>
                                  <IconButton
                                    size="small"
                                    sx={{ 
                                      color: '#ef4444',
                                      '&:hover': {
                                        bgcolor: '#fee2e2',
                                        transform: 'scale(1.1)'
                                      },
                                      transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => window.confirm("Are you sure you want to delete this question?") && deleteQuestion(question.id)}
                                  >
                                    <TrashIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    debugQuestion(question); // Debug log
                                    setExpandedQuestion(expandedQuestion === question.id ? null : question.id);
                                  }}
                                  sx={{
                                    color: '#667eea',
                                    '&:hover': {
                                      bgcolor: '#ede7f6',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <ChevronDownIcon 
                                    sx={{ 
                                      transform: expandedQuestion === question.id ? 'rotate(180deg)' : 'none', 
                                      transition: 'transform 0.3s ease' 
                                    }} 
                                  />
                                </IconButton>
                              </Stack>
                            </TableCell>
                          </TableRow>
                          {expandedQuestion === question.id && (
                            <TableRow>
                              <TableCell colSpan={4} sx={{ bgcolor: '#f8f9fa', py: 3, px: 4 }}>
                                <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                  <CardContent sx={{ p: 3 }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                      Full Question:
                                    </Typography>
                                    <Typography variant="h6" sx={{ mb: 3, lineHeight: 1.6 }}>
                                      {question.question}
                                    </Typography>
                                    
                                    <Divider sx={{ my: 3 }} />
                                    
                                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                                      Answer Options:
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                      <strong>Note:</strong> Correct answer is highlighted in green (Index: {correctIndex})
                                    </Typography>
                                    
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                      {question.options && question.options.map((option, idx) => {
                                        // Check if this is the correct answer
                                        const isCorrect = idx === correctIndex;
                                        
                                        return (
                                          <Paper
                                            key={idx}
                                            elevation={0}
                                            sx={{
                                              p: 2,
                                              border: '2px solid',
                                              borderColor: isCorrect ? '#10b981' : '#e5e7eb',
                                              bgcolor: isCorrect ? '#ecfdf5' : 'white',
                                              borderRadius: 2,
                                              transition: 'all 0.2s ease',
                                              position: 'relative'
                                            }}
                                          >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                              <Box sx={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: isCorrect ? '#10b981' : '#f3f4f6',
                                                color: isCorrect ? 'white' : '#6b7280',
                                                fontWeight: 600,
                                                fontSize: '0.875rem'
                                              }}>
                                                {String.fromCharCode(65 + idx)}
                                              </Box>
                                              <Box sx={{ flex: 1 }}>
                                                <Typography 
                                                  variant="body1" 
                                                  sx={{ 
                                                    fontWeight: isCorrect ? 600 : 400,
                                                    color: isCorrect ? '#065f46' : 'inherit'
                                                  }}
                                                >
                                                  {option || `Option ${idx + 1}`}
                                                </Typography>
                                              </Box>
                                            </Box>
                                            {isCorrect && (
                                              <Box sx={{
                                                position: 'absolute',
                                                right: 12,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5
                                              }}>
                                                <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                                                <Typography variant="caption" fontWeight={600} sx={{ color: '#10b981' }}>
                                                  Correct Answer
                                                </Typography>
                                              </Box>
                                            )}
                                          </Paper>
                                        );
                                      })}
                                    </Box>
                                    
                                    {question.explanation && (
                                      <>
                                        <Divider sx={{ my: 3 }} />
                                        <Box sx={{ 
                                          p: 3, 
                                          bgcolor: '#eff6ff', 
                                          border: '1px solid #bfdbfe',
                                          borderRadius: 2 
                                        }}>
                                          <Typography 
                                            variant="subtitle2" 
                                            fontWeight="bold" 
                                            sx={{ color: '#1e40af', mb: 1 }}
                                          >
                                            📚 Explanation:
                                          </Typography>
                                          <Typography variant="body1" sx={{ color: '#1e40af', lineHeight: 1.7 }}>
                                            {question.explanation}
                                          </Typography>
                                        </Box>
                                      </>
                                    )}
                                    
                                    <Divider sx={{ my: 3 }} />
                                    
                                    <Grid container spacing={3}>
                                      <Grid item xs={12} sm={4}>
                                        <Box sx={{ 
                                          p: 2, 
                                          bgcolor: '#f9fafb', 
                                          borderRadius: 2,
                                          border: '1px solid #e5e7eb'
                                        }}>
                                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                            CATEGORY
                                          </Typography>
                                          <Typography variant="body1" fontWeight="medium" sx={{ mt: 0.5 }}>
                                            {question.skillCategory}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        <Box sx={{ 
                                          p: 2, 
                                          bgcolor: '#f9fafb', 
                                          borderRadius: 2,
                                          border: '1px solid #e5e7eb'
                                        }}>
                                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                            CONTENT
                                          </Typography>
                                          <Typography variant="body1" fontWeight="medium" sx={{ mt: 0.5 }}>
                                            {question.skillContent}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        <Box sx={{ 
                                          p: 2, 
                                          bgcolor: '#f9fafb', 
                                          borderRadius: 2,
                                          border: '1px solid #e5e7eb'
                                        }}>
                                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                            DIFFICULTY
                                          </Typography>
                                          <Typography variant="body1" fontWeight="medium" sx={{ mt: 0.5 }}>
                                            {question.difficulty}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    </Grid>
                                  </CardContent>
                                </Card>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Paper>

        {/* AI Generation Dialog - Same as before */}
        <Dialog 
          open={openAIDialog} 
          onClose={() => setOpenAIDialog(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            py: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                <SparklesIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  AI Question Generator
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                  Let AI create professional test questions for you
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 4, pb: 3 }}>
            <Grid container spacing={3} sx={{ mt:3 }} >
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Skill Category</InputLabel>
                  <Select
                    value={showManualAICategory ? 'manual' : aiConfig.skillCategory}
                    label="Skill Category"
                    onChange={handleAICategoryChange}
                  >
                    <MenuItem value="">Select Category</MenuItem>
                    {skillOptions.map((skill, index) => (
                      <MenuItem key={index} value={skill}>{skill}</MenuItem>
                    ))}
                    <MenuItem value="manual">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EditIcon fontSize="small" />
                        Enter Manually
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
                {showManualAICategory && (
                  <TextField
                    fullWidth
                    label="Enter Custom Category"
                    value={manualAICategory}
                    onChange={handleManualAICategoryChange}
                    required
                    helperText="Enter your own skill category"
                    sx={{ mt: 1 }}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Skill Content</InputLabel>
                  <Select
                    value={showManualAIContent ? 'manual' : aiConfig.skillContent}
                    label="Skill Content"
                    disabled={!aiConfig.skillCategory && !showManualAICategory}
                    onChange={handleAIContentChange}
                  >
                    <MenuItem value="">Select Content</MenuItem>
                    {aiConfig.skillCategory && skillContents[aiConfig.skillCategory]?.map((content, index) => (
                      <MenuItem key={index} value={content}>{content}</MenuItem>
                    ))}
                    <MenuItem value="manual">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EditIcon fontSize="small" />
                        Enter Manually
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
                {showManualAIContent && (
                  <TextField
                    fullWidth
                    label="Enter Custom Content"
                    value={manualAIContent}
                    onChange={handleManualAIContentChange}
                    required
                    helperText="Enter your own skill content"
                    sx={{ mt: 1 }}
                  />
                )}
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Number of Questions"
                  type="number"
                  inputProps={{ min: 1, max: 20 }}
                  value={aiConfig.numberOfQuestions}
                  onChange={(e) => setAiConfig({...aiConfig, numberOfQuestions: parseInt(e.target.value) || 5})}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Difficulty Level</InputLabel>
                  <Select
                    value={aiConfig.difficulty}
                    label="Difficulty Level"
                    onChange={(e) => setAiConfig({...aiConfig, difficulty: e.target.value})}
                  >
                    {difficultyLevels.map((level, index) => (
                      <MenuItem key={index} value={level}>{level}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Card sx={{ 
                  background: 'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
                  border: 'none',
                  borderRadius: 2
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'rgba(102, 126, 234, 0.2)', color: '#667eea' }}>
                        <BookOpenIcon />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#4c1d95' }}>
                          Use Training Video Content
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#5b21b6', mb: 2 }}>
                          Generate questions based on video transcript for more accurate and relevant questions
                        </Typography>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={aiConfig.useVideoTranscript}
                              onChange={(e) => setAiConfig({...aiConfig, useVideoTranscript: e.target.checked})}
                              sx={{
                                color: '#667eea',
                                '&.Mui-checked': { color: '#667eea' }
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#4c1d95' }}>
                              Use video transcript
                            </Typography>
                          }
                        />
                        {aiConfig.useVideoTranscript && (
                          <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel>Select Training Video</InputLabel>
                            <Select
                              value={aiConfig.videoId}
                              label="Select Training Video"
                              onChange={(e) => setAiConfig({...aiConfig, videoId: e.target.value})}
                              sx={{ bgcolor: 'white' }}
                            >
                              <MenuItem value="">Select Training Video</MenuItem>
                              {videos.map((video) => (
                                <MenuItem key={video.id} value={video.id}>{video.title}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button 
              onClick={() => setOpenAIDialog(false)} 
              disabled={aiLoading}
              sx={{ 
                textTransform: 'none',
                fontWeight: 600,
                px: 3
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={aiLoading ? <CircularProgress size={20} color="inherit" /> : <WandIcon />}
              onClick={generateQuestionsWithAI}
              disabled={aiLoading || (!aiConfig.skillCategory && !manualAICategory) || (!aiConfig.skillContent && !manualAIContent)}
              sx={{
                background: 'linear-gradient(45deg, #f093fb 0%, #f5576c 100%)',
                boxShadow: '0 8px 24px rgba(245, 87, 108, 0.3)',
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                '&:hover': {
                  background: 'linear-gradient(45deg, #e082ea 0%, #e4465b 100%)',
                  boxShadow: '0 12px 32px rgba(245, 87, 108, 0.4)'
                }
              }}
            >
              {aiLoading ? 'Generating Questions...' : 'Generate Questions'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Questions Dialog - Same as before */}
        <Dialog 
          open={openBulkDialog} 
          onClose={() => { setOpenBulkDialog(false); resetBulkForm(); }} 
          maxWidth="lg" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            py: 1
          }}>
            <Typography variant="h5" fontWeight="bold">
              Add Multiple Questions
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              Create and manage multiple test questions at once
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={3} sx={{ mb: 3, mt: 3 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Skill Category</InputLabel>
                  <Select
                    value={showManualBulkCategory ? 'manual' : bulkSkillCategory}
                    label="Skill Category"
                    onChange={handleBulkCategoryChange}
                  >
                    <MenuItem value="">Select Category</MenuItem>
                    {skillOptions.map((skill, index) => (
                      <MenuItem key={index} value={skill}>{skill}</MenuItem>
                    ))}
                    <MenuItem value="manual">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EditIcon fontSize="small" />
                        Enter Manually
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
                {showManualBulkCategory && (
                  <TextField
                    fullWidth
                    label="Enter Custom Category"
                    value={manualBulkCategory}
                    onChange={(e) => setManualBulkCategory(e.target.value)}
                    required
                    helperText="Enter your own skill category"
                    sx={{ mt: 1 }}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Skill Content</InputLabel>
                  <Select
                    value={showManualBulkContent ? 'manual' : bulkSkillContent}
                    label="Skill Content"
                    disabled={!bulkSkillCategory && !showManualBulkCategory}
                    onChange={handleBulkContentChange}
                  >
                    <MenuItem value="">Select Content</MenuItem>
                    {bulkSkillCategory && skillContents[bulkSkillCategory]?.map((content, index) => (
                      <MenuItem key={index} value={content}>{content}</MenuItem>
                    ))}
                    <MenuItem value="manual">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EditIcon fontSize="small" />
                        Enter Manually
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
                {showManualBulkContent && (
                  <TextField
                    fullWidth
                    label="Enter Custom Content"
                    value={manualBulkContent}
                    onChange={(e) => setManualBulkContent(e.target.value)}
                    required
                    helperText="Enter your own skill content"
                    sx={{ mt: 1 }}
                  />
                )}
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Questions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {bulkQuestions.length} question{bulkQuestions.length !== 1 ? 's' : ''} added
                </Typography>
              </Box>
              <Button 
                variant="contained"
                startIcon={<PlusIcon />} 
                onClick={addEmptyQuestion}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3
                }}
              >
                Add Question
              </Button>
            </Box>
            
            {bulkQuestions.length === 0 ? (
              <Box sx={{ 
                py: 12, 
                textAlign: 'center', 
                border: '2px dashed #d0d0d0', 
                borderRadius: 3,
                bgcolor: '#fafafa'
              }}>
                <SchoolIcon sx={{ fontSize: 72, color: '#d0d0d0', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No questions added yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click "Add Question" to start creating your test questions
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {bulkQuestions.map((question, qIndex) => {
                  const correctIndex = Number(question.correctAnswer) || 0;
                  return (
                    <Card 
                      key={qIndex} 
                      elevation={2}
                      sx={{ 
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        overflow: 'hidden'
                      }}
                    >
                      <Box 
                        sx={{ 
                          p: 2, 
                          bgcolor: '#f8f9fa',
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          cursor: 'pointer',
                          borderBottom: expandedQuestion === `bulk-${qIndex}` ? '1px solid #e0e0e0' : 'none'
                        }}
                        onClick={() => setExpandedQuestion(expandedQuestion === `bulk-${qIndex}` ? null : `bulk-${qIndex}`)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#667eea', width: 36, height: 36, fontSize: '0.9rem' }}>
                            {qIndex + 1}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Question {qIndex + 1}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {question.question 
                                ? (question.question.substring(0, 60) + (question.question.length > 60 ? '...' : ''))
                                : 'Click to edit question'}
                            </Typography>
                          </Box>
                        </Box>
                        <ChevronDownIcon 
                          sx={{ 
                            transform: expandedQuestion === `bulk-${qIndex}` ? 'rotate(180deg)' : 'none', 
                            transition: 'transform 0.3s ease',
                            color: '#667eea'
                          }} 
                        />
                      </Box>
                      
                      {expandedQuestion === `bulk-${qIndex}` && (
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<TrashIcon />}
                              onClick={() => removeBulkQuestion(qIndex)}
                              sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                              Remove Question
                            </Button>
                          </Box>
                          
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Question Text"
                            required
                            value={question.question}
                            onChange={(e) => handleBulkQuestionChange(qIndex, 'question', e.target.value)}
                            sx={{ mb: 3 }}
                          />
                          
                          <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                Answer Options (Select the correct one by clicking the radio button)
                              </Typography>
                              <Button
                                variant="text"
                                size="small"
                                startIcon={<PlusIcon />}
                                onClick={() => addBulkOption(qIndex)}
                                disabled={question.options.length >= 6}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                              >
                                Add Option
                              </Button>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {question.options.map((option, oIndex) => {
                                const isCorrect = oIndex === correctIndex;
                                return (
                                  <Paper
                                    key={oIndex}
                                    elevation={0}
                                    sx={{
                                      p: 2,
                                      border: '2px solid',
                                      borderColor: isCorrect ? '#10b981' : '#e5e7eb',
                                      bgcolor: isCorrect ? '#ecfdf5' : 'white',
                                      borderRadius: 2
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: isCorrect ? '#10b981' : '#f3f4f6',
                                        color: isCorrect ? 'white' : '#6b7280',
                                        fontWeight: 600,
                                        fontSize: '0.875rem'
                                      }}>
                                        {String.fromCharCode(65 + oIndex)}
                                      </Box>
                                      <TextField
                                        fullWidth
                                        value={option}
                                        onChange={(e) => handleBulkOptionChange(qIndex, oIndex, e.target.value)}
                                        placeholder={`Option ${oIndex + 1}`}
                                        size="small"
                                        variant="standard"
                                        InputProps={{ disableUnderline: true }}
                                        sx={{
                                          flex: 1,
                                          '& .MuiInputBase-input': {
                                            fontWeight: isCorrect ? 600 : 400,
                                            color: isCorrect ? '#065f46' : 'inherit'
                                          }
                                        }}
                                      />
                                      <Radio 
                                        checked={isCorrect}
                                        onChange={() => handleBulkQuestionChange(qIndex, 'correctAnswer', oIndex)}
                                        sx={{
                                          '&.Mui-checked': { color: '#10b981' }
                                        }}
                                      />
                                      {question.options.length > 2 && (
                                        <IconButton
                                          size="small"
                                          onClick={() => removeBulkOption(qIndex, oIndex)}
                                          sx={{ color: '#ef4444' }}
                                        >
                                          <CancelIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                    </Box>
                                    {isCorrect && (
                                      <Box sx={{
                                        mt: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        pl: 4
                                      }}>
                                        <CheckCircleIcon sx={{ color: '#10b981', fontSize: 16 }} />
                                        <Typography variant="caption" fontWeight={600} sx={{ color: '#10b981' }}>
                                          This is currently set as the correct answer
                                        </Typography>
                                      </Box>
                                    )}
                                  </Paper>
                                );
                              })}
                            </Box>
                          </Box>
                          
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Explanation (Optional)"
                            value={question.explanation}
                            onChange={(e) => handleBulkQuestionChange(qIndex, 'explanation', e.target.value)}
                            sx={{ mb: 3 }}
                            placeholder="Provide an explanation for the correct answer"
                          />
                          
                          <FormControl fullWidth>
                            <InputLabel>Difficulty Level</InputLabel>
                            <Select
                              value={question.difficulty}
                              label="Difficulty Level"
                              onChange={(e) => handleBulkQuestionChange(qIndex, 'difficulty', e.target.value)}
                            >
                              {difficultyLevels.map((level, index) => (
                                <MenuItem key={index} value={level}>
                                  <Chip 
                                    label={level} 
                                    size="small"
                                    sx={{
                                      ...getDifficultyColor(level),
                                      fontWeight: 600
                                    }}
                                  />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
            <Button 
              onClick={() => { setOpenBulkDialog(false); resetBulkForm(); }}
              sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={addBulkQuestions}
              disabled={(!bulkSkillCategory && !manualBulkCategory) || (!bulkSkillContent && !manualBulkContent) || bulkQuestions.length === 0}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3d8f 100%)'
                }
              }}
            >
              Add {bulkQuestions.length} Question{bulkQuestions.length !== 1 ? 's' : ''}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Top-Center Floating Alert Snackbar */}
        <Snackbar 
          open={alert.open} 
          autoHideDuration={6000} 
          onClose={() => setAlert(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert 
            onClose={() => setAlert(prev => ({ ...prev, open: false }))} 
            severity={alert.severity}
            sx={{ width: '100%', boxShadow: 3 }}
          >
            {alert.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}

export default TestQuestionManagement;